import { useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { useRouter } from "next/router";
import { CheckoutMode, CheckoutSessionBody } from "../utils/stripe";
import { getStripe, getSubscriptionPaymentSession } from "../lib/stripeRequest";
import { GiftSubscription } from "../context/authContext/reducer";
import { useToast } from "../context/toastContext";
import { giftSubscriptionInitialState } from "../utils/user";
import { useAuth } from "../context/authContext";

export const useGiftActions = () => {
  const [giftSubscription, setGiftSubscription] = useState<GiftSubscription>(
    giftSubscriptionInitialState
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsIsDisabled] = useState(false);

  const router = useRouter();
  const toast = useToast(2500);
  const {
    state: { subscription, user },
  } = useAuth();

  const handlePurchaseGiftClick = useCallback(async () => {
    const discounts = router.query["discounts"] as string;
    const body: CheckoutSessionBody = {
      email: giftSubscription.customerEmail,
      mode: CheckoutMode.payment,
      price_id: router.query["price_id"] as string,
      metadata: {
        giftSubscription,
      },
      discounts: discounts ? JSON.parse(discounts) : null,
      customer_id: subscription?.stripe_id,
    };
    const { data } = await getSubscriptionPaymentSession(body);
    const stripe = await getStripe();
    await stripe.redirectToCheckout({ sessionId: data.id });

  }, [giftSubscription, router.query]);

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setGiftSubscription((giftSubscription) => ({
        ...giftSubscription,
        [e.target.name]: e.target.value,
      }));
    },
    []
  );

  const handleOnRecipientChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const index = e.target.id;
      setGiftSubscription((giftSubscription) => {
        const newArr = giftSubscription.recipient.slice();
        newArr[index][e.target.name] = e.target.value;
        return {
          ...giftSubscription,
          recipient: newArr,
        };
      });
    },
    []
  );

  const validateForm = useCallback(() => {
    for (const [key, value] of Object.entries(giftSubscription)) {
      if (
        (key === "customerEmail" ||
          key === "customerFirstName" ||
          key === "customerLastName") &&
        value === ""
      ) {
        toast("error", "Please make sure every field is filled");
        return false;
      }
    }
    return true;
  }, [giftSubscription, toast]);

  const areValidRecipients = useCallback(() => {
    const areNotEmpty = giftSubscription.recipient.some(
      (gift) =>
        gift.recipientEmail.length > 0 &&
        gift.recipientFirstName.length > 0 &&
        gift.recipientLastName.length > 0
    );
    if (!areNotEmpty) {
      toast("error", "Please make sure every field in recipients are filled");
    }
    return areNotEmpty;
  }, [giftSubscription.recipient, toast]);

  const isValidEmail = useCallback(() => {
    const isValidEmail = giftSubscription.recipient.some((gift) =>
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(gift.recipientEmail)
    );
    if (!isValidEmail) {
      toast("error", "Invalid recipient email address");
    }
    return isValidEmail;
  }, [giftSubscription.recipient, toast]);

  const submitInfo = useCallback(async () => {
    const isValidForm =
      validateForm() && areValidRecipients() && isValidEmail();
    if (isValidForm) {
      try {
        setIsLoading(true);
        setIsIsDisabled(true);
        await handlePurchaseGiftClick();
      } catch (err: AxiosError | any) {
        if (err?.data?.errors?.email)
          toast("error", `Oops, email ${err?.data?.errors?.email}`);
        else if (err?.response?.data) {
          toast("error", `Oops, ${err.response.data.message}`);
        } else toast("error", `Oops, ${err?.data?.error}`);
      } finally {
        setIsLoading(false);
        setIsIsDisabled(false);
      }
    }
  }, [
    areValidRecipients,
    handlePurchaseGiftClick,
    isValidEmail,
    toast,
    validateForm,
  ]);

  const handleEnter = useCallback(async () => {
    submitInfo();
  }, [submitInfo]);

  const handleAddRecipient = useCallback(() => {
    setGiftSubscription((giftSubscription) => {
      return {
        ...giftSubscription,
        recipient: [
          ...giftSubscription.recipient,
          {
            recipientEmail: "",
            recipientFirstName: "",
            recipientLastName: "",
          },
        ],
      };
    });
  }, []);

  useEffect(() => {
    if (user && subscription) {
      setGiftSubscription((giftSubscription) => ({
        ...giftSubscription,
        customerEmail: user.email,
        customerFirstName: user.first_name,
        customerLastName: user.last_name,
      }))
    }
  }, [subscription, user]);

  return {
    giftSubscription,
    submitInfo,
    handleEnter,
    handleOnChange,
    isDisabled,
    isLoading,
    handleAddRecipient,
    handleOnRecipientChange,
  };
};
